import { v4 as uuid } from 'uuid';
import type { AppDispatch } from '@/app/store';
import { addLine } from '@/features/cart/cartSlice';
import { showErrorToast } from '@/features/ui/uiSlice';
import { optionEntries } from '@/features/checkout/ordersApi';
import type { MenuItem, OptionGroup, SelectedOption } from '@/types';
import { computeUnitPrice } from './computeUnitPrice';
import type { ConsumerMode } from './resolveModePrice';
import { thumbPhoto } from './photoVariant';

/** Per-item payload for a shared-cart add (SC-169). guestToken/nickname are baked
 *  into the injected callback by the caller, so only item-level fields flow here. */
export interface SharedAddLineArgs {
  product_uuid: string;
  quantity: number;
  options: { option_value_uuid: string; qty: number }[];
  comment: string;
}

export interface QuickAddToCartArgs {
  dispatch: AppDispatch;
  flyToCart: (el: HTMLElement, src: string) => void;
  restaurantUuid: string;
  item: MenuItem;
  itemGroups: OptionGroup[];
  sourceEl: HTMLElement;
  qty?: number;
  selected?: SelectedOption[];
  /** Free-text kitchen instruction attached to this dish only. */
  comment?: string;
  /** Image flown to the cart when the product itself has no photo (e.g. the restaurant logo). */
  flyImageFallback?: string;
  /** Advisory buffet budget gate. When true, the add is blocked and an error toast is shown (UX-only;
   *  server is always authoritative). Callers derive this from selectBuffetBudgetExceeded. */
  budgetExceeded?: boolean;
  /** i18n message to show in the toast when budget is exceeded. */
  budgetExceededMessage?: string;
  /** SC-169: when present, the table is in shared-cart mode — the add goes to the
   *  server-side shared cart via this callback instead of the local Redux cart.
   *  The caller bakes guestToken/nickname + error handling into the closure. */
  addSharedLine?: (args: SharedAddLineArgs) => void;
  /** SC-218: buffet dine-in session — an "included" product is added at a 0 base
   *  (paid options still count) so the local-cart total mirrors the server quote. */
  isBuffetDineIn?: boolean;
  /** SC-250: fulfillment mode (dine_in / takeaway / delivery) — selects the
   *  per-mode base price so the stored line price matches the displayed one and
   *  the server quote. Omitted → mode-agnostic item.price (unchanged behaviour). */
  mode?: ConsumerMode;
}

/**
 * DF-0518 — les choix d'un plat dans l'ordre de ses questions, pas dans l'ordre des clics. Le panier part
 * tel quel en cuisine : les questions obligatoires préremplies à l'ouverture passaient devant, les
 * garnitures cochées ensuite derrière, et le bon d'une formule à deux bols séparait le bol 1 de ses
 * garnitures. Une question inconnue de `groups` garde sa place, après les autres.
 */
export function inGroupOrder(selected: SelectedOption[], groups: OptionGroup[]): SelectedOption[] {
  const rank = (groupId: string) => {
    const i = groups.findIndex((g) => g.id === groupId);
    return i === -1 ? groups.length : i;
  };
  return selected
    .map((s, i) => ({ s, i }))
    .sort((a, b) => rank(a.s.groupId) - rank(b.s.groupId) || a.i - b.i)
    .map(({ s }) => s);
}

export function quickAddToCart(args: QuickAddToCartArgs): void {
  const {
    dispatch,
    flyToCart,
    restaurantUuid,
    item,
    itemGroups,
    sourceEl,
    qty = 1,
    selected: clicked = [],
    comment = '',
    flyImageFallback,
    budgetExceeded = false,
    budgetExceededMessage,
    addSharedLine,
    isBuffetDineIn = false,
    mode,
  } = args;

  const selected = inGroupOrder(clicked, itemGroups);

  // Fix 6a: Advisory buffet gating — block the add and dispatch an error toast
  // (previously a silent no-op). Server is always authoritative.
  if (budgetExceeded) {
    if (budgetExceededMessage) {
      dispatch(showErrorToast(budgetExceededMessage));
    }
    return;
  }

  flyToCart(sourceEl, thumbPhoto(item.photo) || flyImageFallback || '');

  // SC-169: shared-cart mode — route the add to the server-side shared cart and
  // skip the local Redux cart entirely. Options serialised via the same helper as
  // the place-order path so the two submit routes agree on the wire shape.
  if (addSharedLine) {
    addSharedLine({
      product_uuid: item.id,
      quantity: qty,
      options: optionEntries({ selectedOptions: selected }),
      comment: comment.trim(),
    });
    return;
  }

  dispatch(
    addLine({
      restaurantUuid,
      line: {
        lineId: uuid(),
        itemId: item.id,
        qty,
        selectedOptions: selected,
        unitPrice: computeUnitPrice(item, itemGroups, selected, isBuffetDineIn, mode),
        note: comment.trim() || undefined,
        snapshot: {
          name: item.name,
          ...(item.translations ? { translations: item.translations } : {}),
          photo: item.photo,
          code: item.code,
        },
      },
    }),
  );
}
